import React from 'react';
import IconButton from '@mui/material/IconButton';
import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';
import BrowserUpdatedOutlinedIcon from '@mui/icons-material/BrowserUpdatedOutlined';
import { toJpeg, toPng } from 'html-to-image';
import { useDispatch, useSelector } from 'react-redux';
import { updatePdfSummaryData } from '../../actions/pdfSummaryActions';
import { pdfSummaryNotification } from '../../actions/notificationActions';

function CaptureButtons(props) {
  const dispatch = useDispatch();
  const pdfSummaryData = useSelector((state) => state.pdfSummary);
  const drawerData = useSelector((state) => state.drawer.data);

  const downloadGraph = () => {
    const block = document.getElementById(props.id);
    toJpeg(block, { quality: 1.0, backgroundColor: 'white' }).then(function (dataUrl) {
      const link = document.createElement('a');
      link.download = props.chartName + '.jpeg';
      link.href = dataUrl;
      link.click();
    });
  };

  const downloadCsv = () => {
    let csvContent = '';
    if (Array.isArray(props.tableData)) {
      const firstItem = props.tableData[0];
      if (
        firstItem &&
        ('StockTicker' in firstItem || 'Weight' in firstItem || 'Description' in firstItem)
      ) {
        const headers = props.tableHeaders;
        const rows = props.tableData.map((item, index) => {
          const row = [
            index + 1,
            item.StockTicker || item.OptTicker || '',
            item.Weight ? (parseFloat(item.Weight) * 100).toFixed(2) + '%' : '',
            item.Description || '',
          ];
          return row
            .map((val) => {
              const value = String(val);
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(',');
        });

        csvContent = [headers.join(','), ...rows].join('\n');
      } else {
        const headers = props.tableHeaders.map((header) => header.header || header);
        const rows = props.tableData.map((row) => {
          return props.tableHeaders.map((header) => {
            const key = header.accessorKey || header;
            let value = row[key];
            if (
              value &&
              typeof value === 'string' &&
              (value.includes(',') || value.includes('"') || value.includes('\n'))
            ) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value !== null && value !== undefined ? value : '';
          });
        });
        csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      }
    } else if (props.tableData && typeof props.tableData === 'object') {
      if (
        props.tableHeaders &&
        typeof props.tableHeaders === 'object' &&
        !Array.isArray(props.tableHeaders)
      ) {
        const entries = Object.entries(props.tableData);
        const headers = ['Metric', 'Value'];
        const rows = entries.map(([key, value]) => {
          let formattedValue = Array.isArray(value) ? value.join(', ') : String(value);
          if (
            formattedValue.includes(',') ||
            formattedValue.includes('"') ||
            formattedValue.includes('\n')
          ) {
            formattedValue = `"${formattedValue.replace(/"/g, '""')}"`;
          }
          let formattedKey = key;
          if (
            formattedKey.includes(',') ||
            formattedKey.includes('"') ||
            formattedKey.includes('\n')
          ) {
            formattedKey = `"${formattedKey.replace(/"/g, '""')}"`;
          }
          return `${formattedKey},${formattedValue}`;
        });
        csvContent = [headers.join(','), ...rows].join('\n');
      } else {
        const headers = props.tableHeaders;
        const dataValues = Object.values(props.tableData);
        const firstItem = dataValues[0];
        if (
          firstItem &&
          ('StockTicker' in firstItem || 'Weight' in firstItem || 'Description' in firstItem)
        ) {
          const rows = dataValues.map((item, index) => {
            const row = [
              index + 1,
              item.StockTicker || item.OptTicker || '',
              item.Weight ? (parseFloat(item.Weight) * 100).toFixed(2) + '%' : '',
              item.Description || '',
            ];
            return row
              .map((val) => {
                const value = String(val);
                if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',');
          });

          csvContent = [headers.join(','), ...rows].join('\n');
        } else {
          const updatedData = Object.entries(props.tableData).map(([key, value]) => {
            const updatedValue = Object.entries(value).reduce((acc, [k, v]) => {
              const newKey =
                k === 'imp_vol'
                  ? 'Implied Volatility'
                  : k === 'hist_vol'
                    ? 'Historical Volatility'
                    : k === 'symbol'
                      ? 'Symbol'
                      : k;
              if (newKey === 'Implied Volatility' || newKey === 'Historical Volatility') {
                v = v === 'N.A.' ? 'N.A.' : (parseFloat(v) * 100).toFixed(2) + '%';
              }
              return { ...acc, [newKey]: v };
            }, {});
            return { [key]: { Symbol: value.Symbol, ...updatedValue } };
          });
          const uniqueKeys = Array.from(
            new Set(updatedData.flatMap((obj) => Object.keys(obj[Object.keys(obj)[0]])))
          );
          const rows = uniqueKeys.map((key) => {
            const row = [key];
            updatedData.forEach((obj) => {
              const subObj = obj[Object.keys(obj)[0]];
              let value = String(subObj[key] || '');
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                value = `"${value.replace(/"/g, '""')}"`;
              }
              row.push(value);
            });
            return row.join(',');
          });
          csvContent = [headers.join(','), ...rows].join('\n');
        }
      }
    } else {
      const headers = props.tableHeaders.map((header) => header.header || header);
      const rows = props.tableData.map((row) => {
        return props.tableHeaders.map((header) => {
          const key = header.accessorKey || header;
          let value = row[key];
          if (
            value &&
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"') || value.includes('\n'))
          ) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value !== null && value !== undefined ? value : '';
        });
      });

      csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const cleanChartName = props.chartName.replace(/_\d+/g, '');
    link.setAttribute('href', url);
    link.setAttribute('download', `${cleanChartName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const captureGraphToPdf = async () => {
    if (
      pdfSummaryData &&
      props.chartName in pdfSummaryData &&
      drawerData.portfolioId === pdfSummaryData[props.chartName].activePortfolioId
    ) {
      dispatch(
        pdfSummaryNotification(
          'Analytics Details for this element are already captured for PDF Summary',
          'info'
        )
      );
    } else {
      let pdf_summary = {};
      if (props.elementType === 'chart') {
        const image = await toPng(document.getElementById(props.id));
        pdf_summary = {
          activeTab: props.activeTab,
          ImgData: image,
          chartName: props.chartName,
          userSelectedData: props.userSelectedData,
          displayElement: true,
          customNote: '',
          activePortfolioId: drawerData.portfolioId,
          elementTitle: props.activeTab,
        };
      } else {
        pdf_summary = {
          activeTab: props.activeTab,
          tableHeader: props.tableHeaders,
          tableData: props.tableData,
          chartName: props.chartName,
          userSelectedData: props.userSelectedData,
          displayElement: true,
          totalSums: props.totalSums,
          customNote: '',
          activePortfolioId: drawerData.portfolioId,
          elementTitle: props.activeTab,
        };
      }
      dispatch(updatePdfSummaryData(props.chartName, pdf_summary));
      dispatch(pdfSummaryNotification('Analytics Details captured successfully', 'success'));
    }
  };

  return (
    <>
      {props.elementType === 'chart' ? (
        <div className='sumbtn'>
          <button
            className='ai-btn primary pdf line plain'
            onClick={() => captureGraphToPdf()}
            title='Add to PDF Report'
          >
            <img src='./Assets/Addpdf.svg' alt='Add to PDF' />
          </button>
          <button
            className='ai-btn primary line image plain'
            onClick={() => downloadGraph()}
            title='Download As Image'
          >
            <img src='./Assets/image-download.svg' alt='Download' />
          </button>
        </div>
      ) : (
        <div className='sumbtn'>
          <button
            className='ai-btn primary pdf line plain'
            onClick={() => captureGraphToPdf()}
            title='Add To PDF'
          >
            <img src='./Assets/Addpdf.svg' alt='Add to PDF' />
          </button>
          <button
            className='ai-btn primary csv line plain'
            onClick={() => downloadCsv()}
            title='Download As CSV'
          >
            <img src='./Assets/csv-download.svg' alt='Download CSV' />
          </button>
        </div>
      )}
    </>
  );
}
export default CaptureButtons;
